import { minutesToMillis } from '@ianesthesie-kit/core';
import { InitializationStatus } from '@ianesthesie-kit/redux';

export abstract class CacheManager<TCachable, TCache> {
  private static readonly DEFAULT_GC_TTI = minutesToMillis(4);

  protected cache: TCache;
  private initStatus: InitializationStatus = 'notInitialized';
  private gcTimer?: ReturnType<typeof setTimeout>;

  protected constructor(
    private readonly tti: number = CacheManager.DEFAULT_GC_TTI
  ) {
    this.cache = this.initializeCache();
  }

  public getCache() {
    return this.cache;
  }

  public async get(): Promise<TCachable>;
  public async get<R>(filterFn: (c: TCache) => R): Promise<R>;
  public async get<R>(filterFn?: (c: TCache) => R): Promise<TCachable | R> {
    try {
      await this.initialize();
    } catch (err) {
      // TODO sentry
      // TODO handle error
      return Promise.reject();
    }

    if (!filterFn) {
      return this.denormalize(this.cache);
    }
    return filterFn(this.cache);
  }

  public getInitializationStatus(): InitializationStatus {
    return this.initStatus;
  }

  public isInitialized(): boolean {
    return this.initStatus === 'initialized';
  }

  private initialize(): Promise<void> {
    this.resetGcTimer();
    if (this.initStatus === 'initialized') {
      return Promise.resolve();
    }

    if (this.initStatus === 'error') {
      return Promise.reject();
    }

    if (this.initStatus === 'initializing') {
      return new Promise<void>((resolve) => setTimeout(resolve, 1000)).then(
        () =>
          this.initStatus !== 'error' ? this.initialize() : Promise.reject()
      );
    }

    this.initStatus = 'initializing';

    return this.fetchFresh()
      .then((optionGroups) => {
        this.normalizeAndCache(optionGroups);
        this.initStatus = 'initialized';
      })
      .catch((err) => {
        //TODO sentry report
        //TODO handle error
        console.log(err);
        this.clearTimer();
        this.initStatus = 'error';
      });
  }

  private normalizeAndCache(cachable: TCachable): void {
    this.cache = this.normalize(cachable);
  }

  private resetGcTimer(): void {
    this.clearTimer();
    this.gcTimer = setTimeout(() => this.resetState(), this.tti);
  }

  private resetState(): void {
    this.clearTimer();
    this.garbageCollect();
    this.initStatus = 'notInitialized';
  }

  private garbageCollect(): void {
    this.cache = this.initializeCache();
  }

  private clearTimer(): void {
    if (this.gcTimer) {
      clearTimeout(this.gcTimer);
    }
    this.gcTimer = undefined;
  }

  protected abstract initializeCache(): TCache;
  protected abstract fetchFresh(): Promise<TCachable>;
  protected abstract normalize(el: TCachable): TCache;
  protected abstract denormalize(cache: TCache): TCachable;
}
